import { addDays, startOfDay } from '@if/core/date'
import {
  ECreditApplicationStatus,
  EFinancingCompany,
  EFinancingProgram,
  EIFinanceTeam,
  EProgressionStatus,
  ESourceSystem,
  getELoanPurposeList,
  getEOriginSystemIdList,
  getEProgressionStatusList,
} from '@if/core/types'
import { formatUtcIsoDateTime } from '@if/core/utils'
import {
  ControlledAutocomplete,
  ControlledMultipleSelect,
  ControlledMultipleSelectOption,
  ControlledNumberField,
  ControlledSelect,
} from '@if/mui'
import ClearIcon from '@mui/icons-material/Clear'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Typography,
} from '@mui/material'
import { useGetMerchantById, useSearchMerchants } from '@src/data/api/merchant/merchant-api'
import { getProgramsByCompanyId } from '@src/data/types/FinancingCompanies'
import { usePreferences } from '@src/providers/PreferencesProvider'
import { debounce } from 'lodash-es'
import { ChangeEvent, memo, useEffect, useMemo, useState, type JSX } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { InputTextField } from '../../components'
import {
  useAllPlansForPrograms,
  useCurrentLang,
  useFeatureFlags,
  useFinancingProgramEnum,
  useMerchantTagKeys,
  useMerchantTags,
  useTerritories,
} from '../../data/selectors/app-config-selectors'
import { Merchant } from '../../data/types'
import { CREDIT_DASHBOARD_FILTER_ALL, CreditDashboardFilters } from '../../data/types/CreditDashboardEntry'

interface Props {
  filterValues: CreditDashboardFilters
  resetFilter: () => void
  applyFilter: (data: CreditDashboardFilters) => void
  onCancel: () => void
  open: boolean
}

const MERCHANTS_LIMIT = 25

const isProgressionStatusRequiringAllAppStatuses = (statuses: string[]) =>
  statuses.some((value) =>
    [
      EProgressionStatus.Quotation,
      EProgressionStatus.Submitted,
      EProgressionStatus.Booked,
      EProgressionStatus.WithDrawn,
    ].includes(value as EProgressionStatus),
  )

const SearchFilter = ({ filterValues, resetFilter, applyFilter, onCancel, open }: Props): JSX.Element => {
  const { t } = useTranslation()
  const { selectedCompany } = usePreferences()
  const territories = useTerritories()
  const lang = useCurrentLang()
  const financingProgramEnum = useFinancingProgramEnum()
  const featureFlags = useFeatureFlags()
  const tags = useMerchantTags()
  const tagKeys = useMerchantTagKeys()

  const { control, handleSubmit, reset, setValue } = useForm<CreditDashboardFilters>({
    defaultValues: filterValues,
  })

  useEffect(() => {
    reset(filterValues)
  }, [filterValues, reset])

  // If user picks a progression status that needs to span all credit app statuses,
  // widen the credit-app-status filter to ALL.
  const progressionStatus = useWatch({ control, name: 'progressionStatus' })
  useEffect(() => {
    if (isProgressionStatusRequiringAllAppStatuses(progressionStatus)) {
      setValue('creditApplicationStatus', CREDIT_DASHBOARD_FILTER_ALL)
    }
  }, [progressionStatus, setValue])

  const programsByCompanyId = useMemo(
    () => getProgramsByCompanyId(selectedCompany ? [selectedCompany] : []),
    [selectedCompany],
  )

  // Exclude Auto from the program filter — historical behavior kept from previous implementation.
  const activeProgramsSet = useMemo<Set<EFinancingProgram>>(() => {
    const hiddenPrograms = new Set<EFinancingProgram>([EFinancingProgram.Auto])
    if (!featureFlags.enableEasyCare) hiddenPrograms.add(EFinancingProgram.EasyPay)
    return new Set(programsByCompanyId.filter((id) => !hiddenPrograms.has(id)))
  }, [programsByCompanyId, featureFlags.enableEasyCare])

  const filteredFinancingPrograms = useMemo(
    () => Object.values(financingProgramEnum).filter((program) => activeProgramsSet.has(program.id as EFinancingProgram)),
    [financingProgramEnum, activeProgramsSet],
  )

  const filteredFinancingProgramIds = useMemo(
    () => filteredFinancingPrograms.map((program) => program.id as EFinancingProgram),
    [filteredFinancingPrograms],
  )

  const allPlans = useAllPlansForPrograms(filteredFinancingProgramIds)

  const planItems = useMemo<ControlledMultipleSelectOption<string>[]>(
    () => allPlans.map((plan) => ({ label: plan[lang], value: plan.id })),
    [allPlans, lang],
  )

  const territoryItems = useMemo<ControlledMultipleSelectOption<string>[]>(() => {
    const sourceSystem = selectedCompany === EFinancingCompany.Iceberg ? ESourceSystem.Monolith : ESourceSystem.Alis
    return territories
      .filter((territory) => territory.sourceSystem === sourceSystem)
      .map((item) => ({ label: item.name, value: item.id }))
  }, [territories, selectedCompany])

  const financingProgramItems = useMemo<ControlledMultipleSelectOption<string>[]>(
    () => filteredFinancingPrograms.map((program) => ({ label: program[lang], value: program.id })),
    [filteredFinancingPrograms, lang],
  )

  const loanPurposeItems = useMemo<ControlledMultipleSelectOption<string>[]>(
    () =>
      getELoanPurposeList().map((item) => ({
        label: t(`ELoanPurpose.${item}`, { ns: 'enum' }),
        value: item,
      })),
    [t],
  )

  const progressionStatusItems = useMemo<ControlledMultipleSelectOption<string>[]>(
    () =>
      getEProgressionStatusList().map((item) => ({
        label: t(`EProgressionStatus.${item}`, { ns: 'enum' }),
        value: item,
      })),
    [t],
  )

  const originSystemItems = useMemo<ControlledMultipleSelectOption<string>[]>(
    () =>
      getEOriginSystemIdList().map((id) => ({
        label: t(`EOriginSystemId.${id}`, { ns: 'enum' }),
        value: id,
      })),
    [t],
  )

  const iFinanceTeamOptions = useMemo<JSX.Element[]>(
    () => [
      <MenuItem key={CREDIT_DASHBOARD_FILTER_ALL} value={CREDIT_DASHBOARD_FILTER_ALL}>
        {t('common.all')}
      </MenuItem>,
      <MenuItem key={EIFinanceTeam.IFinance} value={EIFinanceTeam.IFinance}>
        iFinance
      </MenuItem>,
      <MenuItem key={EIFinanceTeam.Medicard} value={EIFinanceTeam.Medicard}>
        Medicard
      </MenuItem>,
    ],
    [t],
  )

  const creditApplicationStatusOptions = useMemo<JSX.Element[]>(
    () => [
      <MenuItem key={CREDIT_DASHBOARD_FILTER_ALL} value={CREDIT_DASHBOARD_FILTER_ALL}>
        {t('common.all')}
      </MenuItem>,
      <MenuItem key={ECreditApplicationStatus.Active} value={ECreditApplicationStatus.Active}>
        {t('ECreditApplicationStatus.active', { ns: 'enum' })}
      </MenuItem>,
      <MenuItem key={ECreditApplicationStatus.Cancelled} value={ECreditApplicationStatus.Cancelled}>
        {t('ECreditApplicationStatus.cancelled', { ns: 'enum' })}
      </MenuItem>,
      <MenuItem key={ECreditApplicationStatus.Completed} value={ECreditApplicationStatus.Completed}>
        {t('ECreditApplicationStatus.completed', { ns: 'enum' })}
      </MenuItem>,
      <MenuItem key={ECreditApplicationStatus.Draft} value={ECreditApplicationStatus.Draft}>
        {t('ECreditApplicationStatus.draft', { ns: 'enum' })}
      </MenuItem>,
      <MenuItem key={ECreditApplicationStatus.Expired} value={ECreditApplicationStatus.Expired}>
        {t('ECreditApplicationStatus.expired', { ns: 'enum' })}
      </MenuItem>,
    ],
    [t],
  )

  const dateRangeOptions = useMemo<JSX.Element[]>(() => {
    const now = new Date()
    const tomorrow = startOfDay(addDays(new Date(), 1))

    const oldRanges = [
      {
        label: t('browseCreditApplications.dataGridHeader.lessThanThreeMonths'),
        value: `${formatUtcIsoDateTime(startOfDay(addDays(new Date(), -90)))}@${formatUtcIsoDateTime(tomorrow)}`,
      },
      {
        label: t('browseCreditApplications.dataGridHeader.lessThanSixMonths'),
        value: `${formatUtcIsoDateTime(startOfDay(addDays(new Date(), -180)))}@${formatUtcIsoDateTime(tomorrow)}`,
      },
      {
        label: t('browseCreditApplications.dataGridHeader.lessThanOneYear'),
        value: `${formatUtcIsoDateTime(startOfDay(addDays(new Date(), -365)))}@${formatUtcIsoDateTime(tomorrow)}`,
      },
      {
        label: t('browseCreditApplications.dataGridHeader.lessThanTwoYears'),
        value: `${formatUtcIsoDateTime(startOfDay(addDays(new Date(), -730)))}@${formatUtcIsoDateTime(tomorrow)}`,
      },
    ]

    const newRanges = [
      {
        label: t('browseCreditApplications.dataGridHeader.lessThanOneHour'),
        value: `${formatUtcIsoDateTime(new Date(now.getTime() - 1 * 60 * 60 * 1000))}@${formatUtcIsoDateTime(now)}`,
      },
      {
        label: t('browseCreditApplications.dataGridHeader.fromOneToTwoHours'),
        value: `${formatUtcIsoDateTime(new Date(now.getTime() - 2 * 60 * 60 * 1000))}@${formatUtcIsoDateTime(
          new Date(now.getTime() - 1 * 60 * 60 * 1000),
        )}`,
      },
      {
        label: t('browseCreditApplications.dataGridHeader.fromTwoToThreeHours'),
        value: `${formatUtcIsoDateTime(new Date(now.getTime() - 3 * 60 * 60 * 1000))}@${formatUtcIsoDateTime(
          new Date(now.getTime() - 2 * 60 * 60 * 1000),
        )}`,
      },
      {
        label: t('browseCreditApplications.dataGridHeader.fromThreeToFourHours'),
        value: `${formatUtcIsoDateTime(new Date(now.getTime() - 4 * 60 * 60 * 1000))}@${formatUtcIsoDateTime(
          new Date(now.getTime() - 3 * 60 * 60 * 1000),
        )}`,
      },
      {
        label: t('browseCreditApplications.dataGridHeader.lessThanOneDay'),
        value: `${formatUtcIsoDateTime(startOfDay(addDays(new Date(), -1)))}@${formatUtcIsoDateTime(now)}`,
      },
      {
        label: t('browseCreditApplications.dataGridHeader.lessThanTwoDays'),
        value: `${formatUtcIsoDateTime(startOfDay(addDays(new Date(), -2)))}@${formatUtcIsoDateTime(now)}`,
      },
    ]

    return [
      <MenuItem key={CREDIT_DASHBOARD_FILTER_ALL} value={CREDIT_DASHBOARD_FILTER_ALL}>
        {t('common.all')}
      </MenuItem>,
      ...[...oldRanges, ...newRanges].map((range) => (
        <MenuItem key={range.value} value={range.value}>
          {range.label}
        </MenuItem>
      )),
    ]
  }, [t])

  // Merchant Autocomplete keeps a raw Controller — the form stores merchantId (string),
  // but the Autocomplete works with full Merchant objects, so they cannot share a value.
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [fetchedMerchant, loadingMerchantById] = useGetMerchantById(filterValues.merchantId)

  const searchDto = useMemo(
    () =>
      debouncedQuery
        ? {
            financingCompanyId: selectedCompany,
            financingProgramId: null,
            nameContains: debouncedQuery,
            limit: MERCHANTS_LIMIT,
          }
        : null,
    [debouncedQuery, selectedCompany],
  )
  const [merchants, loadingMerchantSearch] = useSearchMerchants(searchDto)
  const loadingMerchant = loadingMerchantById || loadingMerchantSearch

  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null)
  useEffect(() => {
    if (fetchedMerchant && !selectedMerchant) setSelectedMerchant(fetchedMerchant)
  }, [fetchedMerchant, selectedMerchant])

  const debouncedSetQuery = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedQuery(value)
      }, 1000),
    [],
  )

  const updateQuery = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    debouncedSetQuery(e.target.value)
  }

  const onSubmit = handleSubmit((data) => {
    applyFilter({ ...filterValues, ...data })
    onCancel()
  })

  return (
    <Dialog
      onClose={onCancel}
      open={open}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="lg"
    >
      <DialogTitle id="alert-dialog-title">{t('common.filter')}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              control={control}
              name="merchantId"
              render={({ field }) => (
                <Autocomplete
                  id="merchantList"
                  options={merchants}
                  onChange={(_event, newMerchant: Merchant | null) => {
                    field.onChange(newMerchant?.id ?? '')
                    setSelectedMerchant(newMerchant)
                  }}
                  value={selectedMerchant}
                  autoHighlight
                  getOptionKey={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  noOptionsText={t('common.searchYieldsNoResults')}
                  renderInput={(merchantList) => (
                    <InputTextField
                      onChange={updateQuery}
                      {...merchantList}
                      label={t('common.merchant')}
                      slotProps={{
                        input: {
                          ...merchantList.InputProps,
                          endAdornment: loadingMerchant ? <CircularProgress color="inherit" size={20} /> : null,
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        color: option.isActive ? 'unset' : 'grey',
                        opacity: option.isActive ? 1 : 0.9,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start !important',
                        textAlign: 'left !important',
                      }}
                    >
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.80rem', color: '#808080' }}>
                        {option.address}, {option.city}, {option.province}
                      </Typography>
                    </Box>
                  )}
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledMultipleSelect
              control={control}
              name="plan"
              label={t('credit.plan')}
              options={planItems}
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledMultipleSelect
              control={control}
              name="territoryIds"
              label={t('browseCreditApplications.dataGridHeader.territory')}
              options={territoryItems}
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledMultipleSelect
              control={control}
              name="financingProgramIds"
              label={t('editCreditApplication.financingProgramType')}
              options={financingProgramItems}
              fullWidth
            />
          </Grid>

          {selectedCompany === EFinancingCompany.IFinance && (
            <Grid size={{ xs: 12, md: 6 }}>
              <ControlledMultipleSelect
                control={control}
                name="loanPurposeIds"
                label={t('editCreditApplication.loanPurpose')}
                options={loanPurposeItems}
                fullWidth
              />
            </Grid>
          )}

          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledSelect
              control={control}
              name="dateRange"
              label={t('common.created')}
              options={dateRangeOptions}
              disableEmptyOption
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledSelect
              control={control}
              name="creditApplicationStatus"
              label={t('browseCreditApplications.dataGridHeader.creditApplicationStatus')}
              options={creditApplicationStatusOptions}
              disableEmptyOption
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledMultipleSelect
              control={control}
              name="progressionStatus"
              label={t('credit.progressionStatus')}
              options={progressionStatusItems}
              fullWidth
            />
          </Grid>

          {selectedCompany === EFinancingCompany.IFinance && (
            <Grid size={{ xs: 12, md: 6 }}>
              <ControlledSelect
                control={control}
                name="team"
                label={t('common.team')}
                options={iFinanceTeamOptions}
                disableEmptyOption
                fullWidth
              />
            </Grid>
          )}

          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledAutocomplete
              control={control}
              name="tag"
              label={t('common.tags')}
              options={tagKeys}
              getOptionLabel={(opt) => (opt ? (tags.find((item) => item.id === opt)?.text ?? opt) : '')}
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledNumberField
              control={control}
              name="minAmountRequested"
              label={t('common.amountRequestedGreaterThan')}
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledMultipleSelect
              control={control}
              name="originSystemIds"
              label={t('common.origin')}
              options={originSystemItems}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ButtonGroup>
          <Button
            variant="outlined"
            startIcon={<ClearIcon fontSize="small" color="error" />}
            onClick={() => {
              resetFilter()
              onCancel()
            }}
          >
            {t('taskManager.reset')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<KeyboardArrowRightIcon fontSize="small" color="success" />}
            onClick={onSubmit}
          >
            {t('taskManager.apply')}
          </Button>
        </ButtonGroup>
      </DialogActions>
    </Dialog>
  )
}
export default memo(SearchFilter)
